import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminService, storeAppUrl } from '../../services/adminApi';
import { GlassCard, GlassPanel, LoadingSpinner } from '../../components/GlassUI';
import {
  FaUsers, FaUserPlus, FaBoxOpen, FaTools, FaGem, FaRupeeSign,
  FaExclamationTriangle, FaArrowRight, FaTachometerAlt, FaCrown,
  FaDatabase, FaCreditCard, FaClock,
} from 'react-icons/fa';

const inr = (n: number) =>
  '₹' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const statusChip = (status: string) => {
  const map: Record<string, string> = {
    ACTIVE: 'bg-emerald-100 text-emerald-700',
    TRIAL: 'bg-blue-100 text-blue-700',
    EXPIRED: 'bg-red-100 text-red-700',
    CANCELLED: 'bg-slate-200 text-slate-600',
    PAST_DUE: 'bg-orange-100 text-orange-700',
  };
  return map[status] || 'bg-slate-200 text-slate-600';
};

const AdminOverview: React.FC = () => {
  const [data, setData] = useState<any>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await adminService.stats());
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !data) {
    return <LoadingSpinner label='Loading admin overview...' className='py-24' />;
  }

  const s = data?.stats || {};
  const subs: any[] = data?.subscriptions || [];
  const ownerSub = subs.find((x) => x.owner?.role === 'ADMIN') || subs[0];
  const alertSubs = subs.filter(
    (x) => x.daysLeft !== null && x.daysLeft <= 7 && x.status !== 'EXPIRED'
  );
  const expiredSubs = subs.filter((x) => x.status === 'EXPIRED');

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3'>
          <span className='w-10 h-10 rounded-xl bg-slate-900 text-amber-400 flex items-center justify-center'>
            <FaTachometerAlt />
          </span>
          Admin Overview
        </h1>
        <p className='text-slate-500 mt-1 ml-13'>Business health, accounts & subscription at a glance</p>
      </div>

      {error && (
        <div className='bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center gap-2'>
          <FaExclamationTriangle /> {error}
        </div>
      )}

      {/* Alerts */}
      {(alertSubs.length > 0 || expiredSubs.length > 0) && (
        <div className='bg-amber-50 border border-amber-300 rounded-2xl px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm'>
          <FaExclamationTriangle className='text-amber-600' />
          <span className='text-amber-800 font-medium'>
            {expiredSubs.length > 0
              ? `${expiredSubs.length} subscription${expiredSubs.length > 1 ? 's are' : ' is'} expired. `
              : ''}
            {alertSubs.length > 0
              ? `${alertSubs.length} subscription${alertSubs.length > 1 ? 's' : ''} renewing within 7 days.`
              : ''}
          </span>
          <Link to='/subscription' className='underline font-semibold text-amber-700 ml-auto'>
            Review plans →
          </Link>
        </div>
      )}

      {/* KPI row */}
      <div className='grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4'>
        {[
          { label: 'Total Accounts', value: s.users || 0, icon: FaUsers, tint: 'bg-slate-900 text-amber-400' },
          { label: 'Customers', value: s.customers || 0, icon: FaUserPlus, tint: 'bg-emerald-500 text-white', sub: `+${s.customersToday || 0} today` },
          { label: 'Orders', value: s.orders || 0, icon: FaBoxOpen, tint: 'bg-indigo-500 text-white', sub: `${s.inProgressOrders || 0} in progress` },
          { label: 'Repairs', value: s.repairs || 0, icon: FaTools, tint: 'bg-rose-500 text-white' },
          { label: 'Old Gold', value: s.oldGold || 0, icon: FaGem, tint: 'bg-amber-500 text-white', sub: `${s.pendingOldGold || 0} pending` },
          { label: 'Loyal Customers', value: s.activeCustomers || 0, icon: FaCrown, tint: 'bg-purple-500 text-white' },
        ].map((c, i) => (
          <GlassCard key={i} className='p-4 md:p-5'>
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center mb-3 ${c.tint}`}>
              <c.icon />
            </div>
            <p className='text-2xl md:text-3xl font-bold text-slate-900 leading-none'>{c.value}</p>
            <p className='text-xs md:text-sm text-slate-500 mt-1.5'>
              {c.label}
              {c.sub && <span className='block text-[11px] text-slate-400'>{c.sub}</span>}
            </p>
          </GlassCard>
        ))}
      </div>

      {/* Money row */}
      <div className='grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4'>
        {[
          { label: 'Total Billing', value: inr(s.totalBilling || 0), tone: 'text-slate-900' },
          { label: 'Amount Collected', value: inr(s.totalCollected || 0), tone: 'text-emerald-600' },
          { label: 'Outstanding', value: inr(s.totalOutstanding || 0), tone: 'text-red-600' },
        ].map((c, i) => (
          <GlassPanel key={i} className='p-4 md:p-5 flex items-center gap-3'>
            <FaRupeeSign className='text-amber-500' />
            <div>
              <p className={`text-xl md:text-2xl font-bold ${c.tone}`}>{c.value}</p>
              <p className='text-xs text-slate-500'>{c.label}</p>
            </div>
          </GlassPanel>
        ))}
      </div>

      {/* Owner subscription + system quick actions */}
      <div className='grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6'>
        <div className='lg:col-span-2'>
          {ownerSub ? (
            <GlassPanel className='p-5 md:p-6 h-full'>
              <div className='flex flex-col sm:flex-row sm:items-center gap-4 mb-4'>
                <div className='flex-1'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <h2 className='text-lg font-bold text-slate-900'>Store Subscription</h2>
                    <span className='text-xs font-semibold text-slate-400'>
                      {ownerSub.owner?.name || 'Owner'}
                    </span>
                  </div>
                  <p className='text-sm text-slate-500'>{ownerSub.owner?.email}</p>
                </div>
                <Link
                  to='/subscription'
                  className='text-sm font-semibold text-amber-700 hover:text-amber-800 flex items-center gap-1 shrink-0'
                >
                  Manage plan <FaArrowRight size={12} />
                </Link>
              </div>
              {ownerSub.exempt ? (
                <div className='rounded-2xl bg-gradient-to-br from-slate-800 to-slate-950 text-white p-4 flex items-center gap-3'>
                  <FaCrown className='text-amber-400 text-xl shrink-0' />
                  <div>
                    <p className='font-bold'>Owner account — no plan required</p>
                    <p className='text-white/60 text-xs'>
                      Unlimited customers, staff and modules. This account never expires.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className='flex items-center gap-3 flex-wrap'>
                    <span className='text-2xl font-bold text-slate-900 flex items-center gap-2'>
                      <FaCrown className='text-amber-500 text-lg' /> {ownerSub.plan}
                    </span>
                    <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${statusChip(ownerSub.status)}`}>
                      {ownerSub.status}
                    </span>
                    <span className='text-xs text-slate-500 flex items-center gap-1'>
                      <FaClock /> Yearly plan
                    </span>
                  </div>
                  <div className='mt-4 flex flex-wrap gap-x-8 gap-y-2 text-sm'>
                    <div>
                      <p className='text-slate-500 text-xs'>Days remaining</p>
                      <p className={`font-bold ${ownerSub.daysLeft !== null && ownerSub.daysLeft <= 7 ? 'text-red-600' : 'text-slate-900'}`}>
                        {ownerSub.daysLeft !== null && ownerSub.daysLeft >= 0
                          ? `${ownerSub.daysLeft} days`
                          : 'Expired'}
                      </p>
                    </div>
                    <div>
                      <p className='text-slate-500 text-xs'>Renews / Expires</p>
                      <p className='font-bold text-slate-900'>
                        {ownerSub.endDate
                          ? new Date(ownerSub.endDate).toLocaleDateString()
                          : ownerSub.trialEnd
                          ? new Date(ownerSub.trialEnd).toLocaleDateString()
                          : '—'}
                      </p>
                    </div>
                    <div>
                      <p className='text-slate-500 text-xs'>Plan type</p>
                      <p className='font-bold text-slate-900'>Yearly · {ownerSub.planName}</p>
                    </div>
                  </div>
                </>
              )}
            </GlassPanel>
          ) : (
            <GlassPanel className='p-6 text-center text-slate-500 h-full flex flex-col items-center justify-center gap-2'>
              <FaCreditCard className='text-3xl text-slate-300' />
              <p>No store subscription yet.</p>
              <Link to='/subscription' className='text-amber-700 font-semibold text-sm underline'>
                Set up a plan
              </Link>
            </GlassPanel>
          )}
        </div>

        {/* Quick panel */}
        <GlassPanel className='p-5 md:p-6 flex flex-col gap-3'>
          <h2 className='font-bold text-slate-900'>Quick Actions</h2>
          <Link to='/users' className='flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700 transition-all'>
            <FaUserPlus className='text-emerald-500' /> Create account / give login
          </Link>
          <Link to='/subscription' className='flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700 transition-all'>
            <FaCreditCard className='text-indigo-500' /> Change plan / extend yearly
          </Link>
          <Link to='/system' className='flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-50 hover:bg-slate-100 border border-slate-200 text-sm font-semibold text-slate-700 transition-all'>
            <FaDatabase className='text-amber-500' /> Backup database now
          </Link>
          <a href={storeAppUrl()} className='flex items-center gap-3 px-4 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-all'>
            <FaBoxOpen className='text-amber-400' /> Open CRM Store <FaArrowRight size={11} className='ml-auto' />
          </a>
        </GlassPanel>
      </div>

      {/* Recent accounts */}
      <div>
        <h2 className='text-lg font-bold text-slate-900 mb-3 flex items-center gap-2'>
          <FaUsers className='text-amber-500' /> Recent Accounts
        </h2>
        <GlassPanel className='p-2 md:p-3'>
          {(data?.recentUsers || []).map((u: any, i: number) => (
            <div
              key={u.id}
              className={`flex items-center gap-3 p-3 ${i > 0 ? 'border-t border-slate-100' : ''}`}
            >
              <div className='w-9 h-9 rounded-full bg-gradient-to-br from-slate-700 to-slate-900 text-white text-sm font-bold flex items-center justify-center shrink-0'>
                {u.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-semibold text-slate-900 truncate'>
                  {u.name} <span className='text-slate-400 font-normal'>· {u.role}</span>
                </p>
                <p className='text-xs text-slate-500 truncate'>{u.email}</p>
              </div>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${statusChip(u.subscription?.status || '—')} shrink-0`}>
                {u.subscription?.status || 'NO PLAN'}
              </span>
              {!u.isActive && (
                <span className='text-[10px] font-bold px-2 py-0.5 rounded-full bg-slate-200 text-slate-600 shrink-0'>
                  BLOCKED
                </span>
              )}
            </div>
          ))}
          {(data?.recentUsers || []).length === 0 && (
            <p className='text-slate-400 text-sm text-center py-6'>No accounts yet</p>
          )}
        </GlassPanel>
      </div>
    </div>
  );
};

export default AdminOverview;
